<template>
  <AppSection top>
    <AppContainer>
      <h1 class="text-primary mb-4">{{ title }}</h1>
      <p class="summary mb-8">
        {{ $tt(`success_page.under_way_${countryKey}`) }}
      </p>
      <div class="wrapper flex">
        <AppImg src="/images/cash-register.jpg" :alt="$t('success_page.cash_register_img')" />
        <div>
          <h3 class="mb-7">{{ $tt('success_page.now_what') }}</h3>
          <div class="flex col gap-5">
            <div v-for="({ header, icon, body }, i) in steps" :key="i" class="flex gap-6">
              <AppIcon :icon size="x-large" color="tertiary" />
              <div>
                <h4>{{ header }}</h4>
                <p v-html="body" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppContainer>
  </AppSection>
</template>

<script setup lang="ts">
import { cacheSuccessVisit, clearCachedResumeToken, getCachedSuccessVisit } from "~/helpers/local-storage";
import { snackbarInjectionKey } from "~/assets/injection-keys";
import { COUPON_COOKIE } from "~/assets/constants";

interface Step {
  header: string;
  icon: string;
  body: string;
}

const snackbar = inject(snackbarInjectionKey)!;
const { tt } = useLocalization();
const { yandexMetrikaLoaded, callYandexReachGoal } = useAnalytics(true);
const { $posthog } = useNuxtApp();
const { stripe } = useStripe();
const { getQueryParam } = useRouting();
const countryKey = getQueryParam('country_key')!;
const { insurance } = useBranch();

const title = tt('success_page.pay_success');
useHead({ title });

useCookie(COUPON_COOKIE).value = null;

const steps: Step[] = [
  {
    header: tt('success_page.step1_header'),
    icon: "tabler:message-up",
    body: tt(`success_page.step1_body_${countryKey}`),
  },
  ...(insurance?.countries.includes(countryKey) ? [{
    header: tt(`success_page.step_insurance_title_${countryKey}`),
    icon: "mdi:shield-check-outline",
    body: tt(`success_page.step_insurance_text_${countryKey}`, { url: insurance.url }),
  }] : []),
  {
    header: tt('success_page.step2_header'),
    icon: "mdi:message-question-outline",
    body: tt('success_page.step2_body'),
  },
  {
    header: tt('success_page.step3_header'),
    icon: "streamline:airport-plane-transit",
    body: tt('success_page.step3_body'),
  }
];

const loading = ref(true);

onMounted(() => {
  clearCachedResumeToken(countryKey);
  reportAnalytics('posthog');
});

watch(stripe, checkPayment);

watchEffect(() => {
  if (yandexMetrikaLoaded.value) {
    reportAnalytics('yandex');
  }
});

async function checkPayment() {
  const processor = getQueryParam('payment_processor');
  let valid = true;
  if (processor == 'stripe') {
    const clientSecret = getQueryParam('payment_intent_client_secret')!;
    valid = await checkStripePaymentStatus(clientSecret);
  }
  if (valid) {
    loading.value = false;
    snackbar.value!.showSuccess('success_page.pay_success');
  } else {
    navigateTo('failure');
  }
}

async function checkStripePaymentStatus(clientSecret: string) {
  const { paymentIntent } = await stripe.value!.retrievePaymentIntent(clientSecret);
  return paymentIntent!.status == 'succeeded';
}

function reportAnalytics(provider: 'yandex' | 'posthog') {
  const formId = getQueryParam('form_id')!;
  const visited = getCachedSuccessVisit(formId);
  if (visited) {
    console.info('Visited success. Skipping report.');
    return;
  }

  if (provider == 'posthog') {
    reportPosthogAnalytics(formId);
  } else if (provider == 'yandex') {
    reportYandexAnalytics(yandexMetrikaLoaded.value);
  }

  cacheSuccessVisit(formId);
}

function reportYandexAnalytics(ymLoaded: boolean) {
  if (!ymLoaded) return;
  callYandexReachGoal();
}

function reportPosthogAnalytics(formId: string) {
  $posthog?.capture('success_page', { form: formId });
}
</script>

<style scoped lang="scss">
@media (min-width: 850px) {
  .wrapper {
    align-items: stretch;
  }

  .wrapper>* {
    flex: 1 1 0;
    min-height: 100%;
  }
}

@media (max-width: 850px) {
  .wrapper {
    flex-direction: column;
  }

  img {
    max-width: 360px;
    width: 100%;
    margin: auto;
  }

  h1,
  h3,
  .summary {
    text-align: center;
  }

  h1 {
    font-size: 32px;
  }

  .summary {
    margin-bottom: var(--space-6) !important;
  }

  h3 {
    margin-bottom: var(--space-6) !important;
  }

  h4 {
    font-size: 20px;
  }
}
</style>